import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useGetTemplateQuery, useUpdateTemplateMutation } from '../../../store/templateLibrary/templateLibrary'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAuthenticatedUser } from '../../../store/auth/authSlice'
import { HeaderContext } from '../../LayoutWithLeftSidebar/HeaderContext'
import { createPortal } from 'react-dom'
import { TemplateEditorHeader } from './TemplateEditorHeader'
import { TemplateEditorBox } from './TemplateEditorBox'
import { Col, Row, Spinner } from 'react-bootstrap'
import { useZocksAuth } from '../../../components/ScopedBlock/hooks'
import { TemplateDetailsBox } from './TemplateDetailsBox'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { getTemplateLibraryTrackingDetails } from './tracking'
import { useAccount } from '../../../common/useAccount'
import { addToast } from '../../../store/toast/toastSlice'
import { TemplateAIChat } from './TemplateAIChat'

export const TemplateEditor: React.FC = () => {
  const dispatch = useAppDispatch()
  const { tracked, trackingClient } = useTracking()
  const user = useAppSelector(selectAuthenticatedUser)
  const { portalRef } = useContext(HeaderContext)
  const { hasScope } = useZocksAuth()
  const hasTemplateAdminScope = hasScope('**', 'tmpl_lib:admin', ['tmpl_lib:admin'])
  const { accountId, account } = useAccount()
  const { templateId = '' } = useParams()
  const location = useLocation()
  const locationState = location.state as { aiAgentId?: string; initialMessages?: Array<{ type: 'user' | 'agent'; message: string }> } | null
  const initialAiAgentId = locationState?.aiAgentId ?? null
  const initialMessages = locationState?.initialMessages
  const { data: templateValue } = useGetTemplateQuery({ accountId, templateId })
  const [updateTemplate, { isLoading }] = useUpdateTemplateMutation()
  const canEditTemplate =
    !account?.enterpriseConfig.lockTemplates &&
    !!templateValue &&
    !['system', 'enterprise'].includes(templateValue.scope) &&
    (templateValue.scope === 'user' || (templateValue.scope === 'account' && hasTemplateAdminScope))

  const [localTemplate, setLocalTemplate] = useState<string | null>(null)
  const [aiIsLoading, setAiIsLoading] = useState(false)
  const currentEditorContentRef = useRef<string | null>(null)
  const handleEditorChange = useCallback((html: string) => {
    currentEditorContentRef.current = html
  }, [])
  const _handleTemplateChange = async (newTemplate: string) => {
    if (!templateValue) {
      return
    }
    await updateTemplate({
      accountId,
      templateId: templateValue.id,
      request: {
        template: {
          template: newTemplate,
        },
      },
    }).unwrap()
    dispatch(addToast({ variant: 'success', title: `"${templateValue.name}" saved` }))
  }

  const handleTemplateChange = tracked(
    _handleTemplateChange,
    {
      eventName: TrackEvent.TEMPLATE_SAVED,
    },
    { condition: !!templateValue, eventDetailsExtractor: () => getTemplateLibraryTrackingDetails(templateValue!) },
  )

  const handleAITemplateUpdate = (newTemplate: string) => {
    setLocalTemplate(newTemplate)
  }

  useEffect(() => {
    setLocalTemplate(null)
    setAiIsLoading(false)
    currentEditorContentRef.current = null
  }, [templateId])

  useEffect(() => {
    if (templateValue?.id && !user?.isGhosted) {
      trackingClient.track({
        eventName: TrackEvent.TEMPLATE_OPENED,
        eventDetails: getTemplateLibraryTrackingDetails(templateValue),
      })
    }
  }, [templateValue?.id, trackingClient, user?.isGhosted])

  return (
    <div className='truly-full-size-content'>
      {portalRef && portalRef.current ? createPortal(<TemplateEditorHeader templateName={templateValue?.name} />, portalRef.current) : null}
      {templateValue ? (
        <Row className='h-100'>
          <Col className='ps-3 pe-0' xs={8}>
            <TemplateEditorBox
              templateValue={localTemplate !== null ? { ...templateValue, template: { ...templateValue.template, template: localTemplate } } : templateValue}
              onSave={handleTemplateChange}
              onChange={handleEditorChange}
              loading={isLoading}
              shouldReset={false}
              readOnly={!canEditTemplate || aiIsLoading}
              showDisabledBanner={!canEditTemplate}
            />
          </Col>
          <Col xs={4} className='zocks-bg-color-gray-100 border-start zocks-p-2.0 d-flex flex-column gap-3'>
            <TemplateDetailsBox templateValue={templateValue} readOnly={!canEditTemplate} />
            <TemplateAIChat
              templateId={templateId}
              currentTemplateRef={currentEditorContentRef}
              onTemplateUpdate={handleAITemplateUpdate}
              onLoadingChange={setAiIsLoading}
              readOnly={!canEditTemplate}
              initialAiAgentId={initialAiAgentId}
              initialMessages={initialMessages}
            />
          </Col>
        </Row>
      ) : (
        <div className='d-flex justify-content-center align-items-center h-100 pt-5'>
          <Spinner />
        </div>
      )}
    </div>
  )
}
